import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { QRCodeCanvas } from 'qrcode.react';
import propertyService from '../../services/propertyService';
import Alert from '../common/Alert';
import LoadingSpinner from '../common/LoadingSpinner';
import '../../pages/ApplicantPages.css';

// The property's shareable application link and QR code. Applicants who use it still have to open a
// personal link emailed to them, so nobody can apply in someone else's name.
const ApplyLinkCard = ({ propertyId, disabled = false, disabledReason = '', onChange }) => {
  const [link, setLink] = useState(null);
  const [notice, setNotice] = useState(null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const canvasRef = useRef(null);

  const load = useCallback(async () => {
    setLink(null);
    if (!propertyId) return;
    try {
      setLink(await propertyService.applyLink(propertyId));
    } catch (err) {
      setNotice({ type: 'error', message: err.response?.data?.message || 'The application link could not be loaded.' });
    }
  }, [propertyId]);

  useEffect(() => {
    load();
  }, [load]);

  const toggle = async (enable) => {
    setBusy(true);
    setNotice(null);
    try {
      const result = enable ? await propertyService.enableApplyLink(propertyId) : await propertyService.disableApplyLink(propertyId);
      setLink({ enabled: result.enabled, url: result.url || null });
      if (onChange) onChange(result.enabled);
    } catch (err) {
      setNotice({ type: 'error', message: err.response?.data?.message || 'The link could not be changed.' });
    } finally {
      setBusy(false);
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link.url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      setNotice({ type: 'warning', message: 'Copying was blocked by the browser. Select the link and copy it manually.' });
    }
  };

  const downloadQr = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.toBlob((blob) => {
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'application-link-qr.png';
      a.click();
      URL.revokeObjectURL(url);
    });
  };

  return (
    <section className="ap-card" aria-labelledby="apply-link-title">
      <h2 id="apply-link-title">Application link</h2>
      {notice && <Alert type={notice.type} message={notice.message} dismissible onClose={() => setNotice(null)} />}
      {!link ? (
        <LoadingSpinner />
      ) : link.enabled ? (
        <>
          {disabled && <Alert type="warning" message={disabledReason} />}
          <p className="ap-muted">
            Put this link or QR code on a listing site, a message or a board. Applicants enter their email and get a
            personal link, so nothing is shown or created for them until they open it.
          </p>
          <div className="ap-qr">
            <QRCodeCanvas
              ref={canvasRef}
              value={link.url}
              size={512}
              marginSize={2}
              style={{ width: 160, height: 160 }}
              title="Application link QR code"
            />
            <div className="ap-link-box" style={{ flex: 1 }}>
              <input readOnly value={link.url} aria-label="Application link" onFocus={(e) => e.target.select()} />
              <button type="button" className="btn-primary ap-button" onClick={copy}>{copied ? 'Copied' : 'Copy link'}</button>
              <button type="button" className="ap-button ap-button--ghost" onClick={downloadQr}>Download QR code</button>
              <button type="button" className="ap-button ap-button--ghost" onClick={() => toggle(false)} disabled={busy}>Turn off</button>
            </div>
          </div>
        </>
      ) : (
        <>
          <p className="ap-muted">The link is off. Turn it on to let applicants start an application themselves.</p>
          <button type="button" className="btn-primary ap-button" onClick={() => toggle(true)} disabled={busy}>
            Turn on application link
          </button>
        </>
      )}
    </section>
  );
};

ApplyLinkCard.propTypes = {
  propertyId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  disabled: PropTypes.bool,
  disabledReason: PropTypes.string,
  onChange: PropTypes.func
};

export default ApplyLinkCard;
